import { isValidImageDataUrl } from './imageValidation';
import { extractImageUrls } from './imageExtraction';

// Enhanced image processing with Unsplash data support
export interface ProcessedImageContent {
  text: string;
  images: string[];
  imageUrls: string[];
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

// Enhanced function to detect feedback analysis content using same markers as textCleaningUtils
const isFeedbackAnalysis = (text: string): boolean => {
  const feedbackMarkers = [
    '## 📊', // Impressions Overview
    '## ✅', // Accurate Impressions
    '## ❌', // Mismatches
    '## 🔄', // Partial Connections
    '## 💡', // Additional Observations
    'Impressions Overview', // Fallback text check
    'Accurate Impressions', // Fallback text check
    'Analysis of User Notes' // Fallback text check
  ];
  return feedbackMarkers.some(marker => text.includes(marker));
};

export const processContentWithImages = (text: string, processContentForLinks: (text: string) => string): ProcessedImageContent => {
  console.log('🖼️ imageProcessing: processContentWithImages called with text length:', text.length);
  console.log('🖼️ imageProcessing: Text preview (first 200 chars):', text.substring(0, 200));
  
  // Check if this is feedback analysis content and preserve formatting
  const isFeedback = isFeedbackAnalysis(text);
  console.log('🖼️ imageProcessing: Is feedback analysis content?', isFeedback);
  
  // CRITICAL: Detect welcome message and preserve ALL formatting
  const isWelcomeMessage = text.includes('Welcome to RemoteViz!') && text.includes('Your journey into remote viewing starts here');
  if (isWelcomeMessage) {
    console.log('🏡 imageProcessing: Welcome message detected - preserving ALL formatting');
    return { text, images: [], imageUrls: [] }; // Return completely unchanged with empty arrays
  }
  
  let processedText = text;
  const images: string[] = [];
  const imageUrls: string[] = [];
  let unsplashData: any = undefined;

  // Extract and remove Unsplash data if present
  const unsplashDataMatch = processedText.match(/UNSPLASH_DATA:({.*?})/);
  if (unsplashDataMatch) {
    try {
      unsplashData = JSON.parse(unsplashDataMatch[1]);
      processedText = processedText.replace(/UNSPLASH_DATA:{.*?}/g, '').trim();
      console.log('📊 IMAGE-PROCESSING: Extracted Unsplash data:', unsplashData);
    } catch (error) {
      console.error('❌ IMAGE-PROCESSING: Failed to parse Unsplash data:', error);
    }
  }

  // Process IMAGE_URL markers and extract URLs
  const imageUrlMatches = processedText.match(/IMAGE_URL:(https?:\/\/[^\s]+)/g);
  if (imageUrlMatches) {
    imageUrlMatches.forEach(match => {
      const url = match.replace('IMAGE_URL:', '');
      imageUrls.push(url);
      console.log('🖼️ IMAGE-PROCESSING: Extracted image URL:', url);
    });
    
    // Remove IMAGE_URL markers from text
    processedText = processedText.replace(/IMAGE_URL:https?:\/\/[^\s]+/g, '').trim();
  }
  
  // Check for the new image pattern: [Image: filename] followed by base64 data
  const newImagePattern = /\[Image: ([^\]]+)\]\s*(data:image\/[^;]+;base64,[^\s\n]+)/g;
  
  // Check for multiple image patterns that might be in the content
  const imagePatterns = [
    // Pattern for new format: [Image: filename] data:image/...
    newImagePattern,
    // Pattern for older "Attached file(s):" format
    /Attached file\(s\):\s*(data:image\/[^;]+;base64,[^)]+)/g,
    // Pattern for standalone base64 images
    /(data:image\/[^;]+;base64,[^\s\n]+)/g
  ];
  
  // Process each pattern
  imagePatterns.forEach((pattern, index) => {
    const matches = [...processedText.matchAll(pattern)];
    console.log(`🖼️ imageProcessing: Found matches with pattern ${index}:`, matches.length);
    
    matches.forEach(match => {
      // Get the base64 data (might be in different capture groups depending on pattern)
      let base64Data;
      if (index === 0) {
        // New pattern: [Image: filename] data:image/...
        base64Data = match[2];
        console.log('🖼️ imageProcessing: Processing new format image:', match[1]);
      } else {
        // Other patterns
        base64Data = match[2] || match[1];
      }
      
      console.log('🖼️ imageProcessing: Validating base64 data:', base64Data ? 'found' : 'missing');
      
      if (base64Data && isValidImageDataUrl(base64Data)) {
        // Only add to images array if it's not already there (prevent duplicates)
        if (!images.includes(base64Data)) {
          images.push(base64Data);
          console.log('🖼️ imageProcessing: Added valid base64 image, total images:', images.length);
        } else {
          console.log('🖼️ imageProcessing: Skipped duplicate base64 image');
        }
        
        // Remove the entire match from text
        processedText = processedText.replace(match[0], '').trim();
      } else {
        console.log('🖼️ imageProcessing: Rejected invalid base64 image data');
      }
    });
  });
  
  // If no base64 images found and no IMAGE_URL found, check for regular image URLs
  if (images.length === 0 && imageUrls.length === 0) {
    console.log('🖼️ imageProcessing: No images found, checking for URLs');
    const urlResult = extractImageUrls(text);
    return {
      text: urlResult.text,
      images: urlResult.images || [],
      imageUrls: urlResult.imageUrls || [],
      unsplashData
    };
  }
  
  console.log('🖼️ imageProcessing: Found images:', { base64: images.length, urls: imageUrls.length });
  console.log('🖼️ imageProcessing: Cleaned text length:', processedText.length);
  
  // For feedback analysis, do NOT process content for links - preserve raw markdown
  if (isFeedback) {
    console.log('🖼️ imageProcessing: Preserving feedback analysis - skipping processContentForLinks');
    return { 
      text: processedText, 
      images,
      imageUrls,
      unsplashData
    };
  } else {
    // For non-feedback content, apply normal link processing
    console.log('🖼️ imageProcessing: Applying normal link processing for non-feedback content');
    return { 
      text: processContentForLinks(processedText), 
      images,
      imageUrls,
      unsplashData
    };
  }
};
